import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Calculator, ClipboardCheck, Archive, ChevronRight, ChevronLeft,
  Save, Download, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet, Wand2, CircleSlash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// Download mit Anmeldung (window.open schickt kein Token mit)
const oeffneMitLogin = async (url) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const href = window.URL.createObjectURL(res.data);
    const name = (res.headers["content-disposition"] || "").match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i)?.[1];
    const a = document.createElement("a");
    a.href = href;
    if (name) a.download = decodeURIComponent(name); else a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  } catch (e) {
    toast.error("Download fehlgeschlagen");
  }
};

const STEPS = [
  { id: 1, label: "Stammdaten",      icon: FileText,      desc: "AZAV-Pflichtfelder (KldB, Durchführung, Standort)" },
  { id: 2, label: "Kosten",          icon: Calculator,    desc: "Kostenkalkulation nach BA-Empfehlung" },
  { id: 3, label: "Self-Check",      icon: ClipboardCheck,desc: "Checkliste 09PACL114 vor Einreichung" },
  { id: 4, label: "Maßnahmenliste",  icon: FileSpreadsheet,desc: "Excel-Export 09PAFB47 (27 Spalten)" },
  { id: 5, label: "Antragsmappe",    icon: Archive,       desc: "ZIP-Bundle für die Fachkundige Stelle (FKS)" },
];

// -------- Helper: Stammdaten-Form --------
const StammdatenStep = ({ kursId, onChange }) => {
  const [data, setData] = useState(null);
  const [dozenten, setDozenten] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!kursId) return;
    axios.get(`${API}/azav/kurs-stammdaten/${kursId}`)
      .then(r => { setData(r.data); onChange && onChange(r.data); })
      .catch(() => toast.error("Stammdaten konnten nicht geladen werden"));
    axios.get(`${API}/azav/antrag/dozenten-liste`)
      .then(r => setDozenten(r.data?.items || []))
      .catch(() => {});
  }, [kursId]); // eslint-disable-line

  const set = (k, v) => {
    const next = { ...data, [k]: v };
    setData(next);
    onChange && onChange(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/azav/kurs-stammdaten/${kursId}`, data);
      toast.success("Stammdaten gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-6 text-center text-gray-500"><Loader2 className="inline animate-spin mr-2" />Lade …</div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Maßnahme</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Maßnahme-Titel</Label>
            <Input data-testid="wiz-titel" value={data.massnahme_titel || ""} onChange={e => set("massnahme_titel", e.target.value)} />
          </div>
          <div>
            <Label>Maßnahmenummer (von FKS)</Label>
            <Input data-testid="wiz-nummer" placeholder="z. B. 364 1234 2026" value={data.massnahmenummer || ""} onChange={e => set("massnahmenummer", e.target.value)} />
          </div>
          <div>
            <Label>KldB-2010-Code (5-stellig)</Label>
            <Input data-testid="wiz-kldb-code" value={data.kldb_code || ""} onChange={e => set("kldb_code", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>KldB-Bezeichnung</Label>
            <Input data-testid="wiz-kldb-bez" value={data.kldb_bezeichnung || ""} onChange={e => set("kldb_bezeichnung", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Zielgruppe</Label>
            <Textarea data-testid="wiz-zielgruppe" value={data.zielgruppe || ""} onChange={e => set("zielgruppe", e.target.value)} rows={2} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Durchführung</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Art</Label>
            <Select value={data.durchfuehrungsart || "Vollzeit"} onValueChange={v => set("durchfuehrungsart", v)}>
              <SelectTrigger data-testid="wiz-art"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                <SelectItem value="Teilzeit">Teilzeit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Form</Label>
            <Select value={data.durchfuehrungsform || "Präsenz"} onValueChange={v => set("durchfuehrungsform", v)}>
              <SelectTrigger data-testid="wiz-form"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Präsenz">Präsenz</SelectItem>
                <SelectItem value="Online">Online (synchron)</SelectItem>
                <SelectItem value="Blended-Online">Blended-Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Asynchron-Anteil (%)</Label>
            <Input data-testid="wiz-async" type="number" step="0.1" value={data.asynchron_prozent ?? 0} onChange={e => set("asynchron_prozent", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label>UE gesamt</Label>
            <Input data-testid="wiz-ue-gesamt" type="number" value={data.ue_gesamt ?? 0} onChange={e => set("ue_gesamt", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <Label>UE Theorie</Label>
            <Input data-testid="wiz-ue-theorie" type="number" value={data.ue_theorie ?? 0} onChange={e => set("ue_theorie", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <Label>UE Praxis</Label>
            <Input data-testid="wiz-ue-praxis" type="number" value={data.ue_praxis ?? 0} onChange={e => set("ue_praxis", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <Label>Dauer (Wochen)</Label>
            <Input data-testid="wiz-wochen" type="number" step="0.1" value={data.dauer_wochen ?? 0} onChange={e => set("dauer_wochen", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label>UE/Woche</Label>
            <Input data-testid="wiz-ue-wo" type="number" step="0.5" value={data.ue_pro_woche ?? 0} onChange={e => set("ue_pro_woche", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <Label>BG-fähig</Label>
            <Select value={data.bg_faehig ? "ja" : "nein"} onValueChange={v => set("bg_faehig", v === "ja")}>
              <SelectTrigger data-testid="wiz-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ja">Ja</SelectItem>
                <SelectItem value="nein">Nein</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>TN min</Label>
            <Input data-testid="wiz-tn-min" type="number" value={data.teilnehmer_min ?? 6} onChange={e => set("teilnehmer_min", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <Label>TN max</Label>
            <Input data-testid="wiz-tn-max" type="number" value={data.teilnehmer_max ?? 16} onChange={e => set("teilnehmer_max", parseInt(e.target.value) || 0)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lehrkraft / Dozentin</CardTitle>
          <CardDescription>
            Die ausgewählten Lehrkräfte erscheinen mit ihren echten Qualifikationen
            (aus der Personalakte) in § 7 des Maßnahmekonzepts.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hauptdozentin */}
            <div>
              <Label>Hauptdozentin / Hauptdozent</Label>
              <Select value={data.dozentin_id || "_none"} onValueChange={v => set("dozentin_id", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="wiz-dozentin"><SelectValue placeholder="— keine zuordnen —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— keine zuordnen (generischer Text) —</SelectItem>
                  {dozenten.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} {d.qualifikationen?.length ? `· ${d.qualifikationen.length} Qualifikation(en)` : "· (keine Qualis hinterlegt)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {data.dozentin_id && (() => {
                const d = dozenten.find(x => x.id === data.dozentin_id);
                if (!d) return null;
                return (
                  <div className="mt-2 p-2 rounded bg-emerald-50 border border-emerald-200 text-xs">
                    <div className="font-semibold mb-1">{d.name}</div>
                    {d.qualifikationen?.length ? (
                      <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                        {d.qualifikationen.map((q, i) => <li key={i}>{q}</li>)}
                      </ul>
                    ) : (
                      <div className="text-amber-700">
                        Keine Qualifikationen — bitte in der Personalakte-Verwaltung ergänzen.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Vertretung */}
            <div>
              <Label>Vertretung</Label>
              <Select value={data.vertretung_dozentin_id || "_none"} onValueChange={v => set("vertretung_dozentin_id", v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="wiz-vertretung"><SelectValue placeholder="— keine Vertretung —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— keine Vertretung —</SelectItem>
                  {dozenten
                    .filter(d => d.id !== data.dozentin_id)
                    .map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} {d.qualifikationen?.length ? `· ${d.qualifikationen.length} Qualifikation(en)` : "· (keine Qualis hinterlegt)"}
                      </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {data.vertretung_dozentin_id && (() => {
                const d = dozenten.find(x => x.id === data.vertretung_dozentin_id);
                if (!d) return null;
                return (
                  <div className="mt-2 p-2 rounded bg-blue-50 border border-blue-200 text-xs">
                    <div className="font-semibold mb-1">{d.name}</div>
                    {d.qualifikationen?.length ? (
                      <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                        {d.qualifikationen.map((q, i) => <li key={i}>{q}</li>)}
                      </ul>
                    ) : (
                      <div className="text-amber-700">
                        Keine Qualifikationen — bitte in der Personalakte-Verwaltung ergänzen.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {dozenten.length === 0 && (
              <div className="md:col-span-2 text-xs text-amber-700">
                Keine Dozent:innen in der Personalakte gefunden. Bitte zuerst unter
                &bdquo;Personalakten&ldquo; eine Person mit Rolle &bdquo;dozent&ldquo; anlegen.
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Standort & Abschluss</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Standort-Name</Label>
            <Input data-testid="wiz-stand-name" value={data.standort_name || ""} onChange={e => set("standort_name", e.target.value)} />
          </div>
          <div>
            <Label>Strasse</Label>
            <Input data-testid="wiz-stand-strasse" value={data.standort_strasse || ""} onChange={e => set("standort_strasse", e.target.value)} />
          </div>
          <div>
            <Label>PLZ</Label>
            <Input data-testid="wiz-stand-plz" value={data.standort_plz || ""} onChange={e => set("standort_plz", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Ort</Label>
            <Input data-testid="wiz-stand-ort" value={data.standort_ort || ""} onChange={e => set("standort_ort", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label>Abschlussart</Label>
            <Input data-testid="wiz-abschluss" value={data.abschlussart || ""} onChange={e => set("abschlussart", e.target.value)} />
          </div>
          <div>
            <Label>Prüfende Stelle</Label>
            <Input data-testid="wiz-pruefer" value={data.pruefende_stelle || ""} onChange={e => set("pruefende_stelle", e.target.value)} />
          </div>
          <div>
            <Label>Beginn (geplant)</Label>
            <Input data-testid="wiz-beginn" type="date" value={data.massnahme_beginn_geplant || ""} onChange={e => set("massnahme_beginn_geplant", e.target.value)} />
          </div>
          <div>
            <Label>Ende (geplant)</Label>
            <Input data-testid="wiz-ende" type="date" value={data.massnahme_ende_geplant || ""} onChange={e => set("massnahme_ende_geplant", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="wiz-stammdaten-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Stammdaten speichern
        </Button>
      </div>
    </div>
  );
};

// -------- Helper: Kostenkalkulation --------
const defaultKalk = {
  ue_theorie: 0, ue_praxis: 0, std_betriebliche_lernphase: 0, dauer_wochen: 0, tn_anzahl: 12,
  honorar_eur_pro_ue: 55, einzelcoaching_ue_pro_tn: 0, vor_nachbereitung_prozent: 20,
  besuche_pro_tn: 0, std_pro_besuch: 0, stundensatz_besuch: 0, reisekosten_pro_besuch: 0,
  sozpaed_std_gesamt: 0, sozpaed_stundensatz: 0,
  raumausstattung_eur_pro_woche: 0, pc_technik_eur_pro_woche: 0,
  lernmaterial_eur_pro_tn: 0, schulungsunterlagen_eur_pro_tn: 0, literatur_eur_pro_tn: 0, verbrauchsmaterial_eur: 0,
  raumkosten_eur_pro_ue: 0, pruefungsgebuehr_eur_pro_tn: 0,
  verwaltung_eur_pauschal: 0, eignungsfeststellung_eur_pro_tn: 0,
};

const KostenStep = ({ kursId, stammdaten }) => {
  const [inp, setInp] = useState(defaultKalk);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSavedId, setLastSavedId] = useState(null);

  // Übernahme aus Stammdaten beim ersten Laden
  useEffect(() => {
    if (!stammdaten) return;
    setInp(p => ({
      ...p,
      ue_theorie: stammdaten.ue_theorie || stammdaten.ue_gesamt || p.ue_theorie,
      ue_praxis: stammdaten.ue_praxis || p.ue_praxis,
      dauer_wochen: stammdaten.dauer_wochen || p.dauer_wochen,
      tn_anzahl: stammdaten.teilnehmer_max || p.tn_anzahl,
    }));
  }, [stammdaten]);

  // Letzte gespeicherte Kalkulation laden
  useEffect(() => {
    axios.get(`${API}/azav/kostenkalkulation`)
      .then(r => {
        const mine = (r.data || []).filter(k => k.kurs_id === kursId);
        if (mine[0]) {
          setInp(p => ({ ...p, ...mine[0].input }));
          setResult(mine[0]);
          setLastSavedId(mine[0].id);
        }
      })
      .catch(() => {});
  }, [kursId]);

  const set = (k, v) => setInp(p => ({ ...p, [k]: v }));

  const berechnen = async () => {
    setLoading(true);
    try {
      const payload = {
        kurs_id: kursId,
        massnahme_titel: stammdaten?.massnahme_titel || "",
        massnahmenummer: stammdaten?.massnahmenummer || "",
        ...inp,
      };
      const r = await axios.post(`${API}/azav/kostenkalkulation`, payload);
      setResult(r.data);
      setLastSavedId(r.data.id);
      toast.success(`Kalkulation gespeichert — Preis pro TN: ${r.data.preis_pro_tn?.toFixed(2)} €`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Berechnung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const pdf = () => {
    if (!lastSavedId) return;
    oeffneMitLogin(`${API}/azav/kostenkalkulation/${lastSavedId}/pdf`);
  };

  const FIELD_GROUPS = [
    { title: "§ 1 Maßnahme-Eckdaten", fields: [
      ["ue_theorie", "UE Theorie"], ["ue_praxis", "UE Praxis"],
      ["dauer_wochen", "Dauer (Wochen)"], ["tn_anzahl", "TN-Anzahl"],
    ]},
    { title: "§ 2 Personalkosten", fields: [
      ["honorar_eur_pro_ue", "Honorar €/UE"],
      ["einzelcoaching_ue_pro_tn", "Einzelcoaching UE/TN"],
      ["vor_nachbereitung_prozent", "Vor-/Nachbereitung (%)"],
    ]},
    { title: "§ 3 Betreuung betriebl. Lernphase", fields: [
      ["besuche_pro_tn", "Besuche pro TN"],
      ["std_pro_besuch", "Std/Besuch"],
      ["stundensatz_besuch", "Stundensatz €"],
      ["reisekosten_pro_besuch", "Reisekosten €/Besuch"],
    ]},
    { title: "§ 4 Sozialpädagogik (optional)", fields: [
      ["sozpaed_std_gesamt", "Stunden gesamt"],
      ["sozpaed_stundensatz", "Stundensatz €"],
    ]},
    { title: "§ 5 Ausbildungsmittel", fields: [
      ["raumausstattung_eur_pro_woche", "Raumausstattung €/Wo"],
      ["pc_technik_eur_pro_woche", "PC/Technik €/Wo"],
      ["lernmaterial_eur_pro_tn", "Lernmaterial €/TN"],
      ["schulungsunterlagen_eur_pro_tn", "Unterlagen €/TN"],
      ["literatur_eur_pro_tn", "Literatur €/TN"],
      ["verbrauchsmaterial_eur", "Verbrauchsmat. €"],
    ]},
    { title: "§ 6–9 Restkosten", fields: [
      ["raumkosten_eur_pro_ue", "Raumkosten €/UE"],
      ["pruefungsgebuehr_eur_pro_tn", "Prüfung €/TN"],
      ["verwaltung_eur_pauschal", "Verwaltung € (pauschal)"],
      ["eignungsfeststellung_eur_pro_tn", "Eignungsfeststellung €/TN"],
    ]},
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {FIELD_GROUPS.map(g => (
          <Card key={g.title}>
            <CardHeader className="pb-3"><CardTitle className="text-base">{g.title}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {g.fields.map(([k, lbl]) => (
                <div key={k}>
                  <Label className="text-xs">{lbl}</Label>
                  <Input data-testid={`kalk-${k}`} type="number" step="0.01" value={inp[k] ?? 0} onChange={e => set(k, parseFloat(e.target.value) || 0)} />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
        <div className="flex gap-3">
          <Button onClick={berechnen} disabled={loading} data-testid="kalk-berechnen">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
            Berechnen & speichern
          </Button>
          {lastSavedId && (
            <Button variant="outline" onClick={pdf} data-testid="kalk-pdf"><Download className="w-4 h-4 mr-2" />PDF herunterladen</Button>
          )}
        </div>
      </div>

      <div>
        <Card className="sticky top-4">
          <CardHeader>
            <CardTitle className="text-base">Ergebnis</CardTitle>
            <CardDescription>Live-Aufschlüsselung der Kalkulation</CardDescription>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-sm text-gray-500 py-8 text-center">Noch keine Berechnung vorhanden.</div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Gesamtkosten</div><div className="text-right font-semibold">{result.gesamtkosten?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                  <div className="text-gray-500">Preis pro TN</div><div className="text-right font-semibold text-emerald-600">{result.preis_pro_tn?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                  <div className="text-gray-500">€/UE/TN</div><div className="text-right">{result.preis_pro_ue_pro_tn?.toFixed(2)} €</div>
                  <div className="text-gray-500">DKS-Status</div>
                  <div className="text-right">
                    <Badge variant={result.dks_status === "im Rahmen" ? "default" : "destructive"} className="text-xs">{result.dks_status}</Badge>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="text-xs text-gray-500 mb-2">Aufschlüsselung</div>
                  {result.aufschluesselung?.map(a => (
                    <div key={a.posten} className="flex justify-between text-xs py-1">
                      <span className="truncate pr-2">{a.posten}</span>
                      <span className="tabular-nums">{a.summe?.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € <span className="text-gray-400">({a.anteil_pct}%)</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// -------- Helper: Self-Check --------
const SelfCheckStep = ({ kursId }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/azav/antrag/${kursId}/self-check`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Self-Check nicht ladbar"));
  };
  useEffect(() => { load(); }, [kursId]); // eslint-disable-line

  const setAnswer = (nr, key, value) => {
    setData(prev => ({
      ...prev,
      items: prev.items.map(it => it.nr === nr ? { ...it, [key]: value } : it),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = data.items.map(it => ({ nr: it.nr, antwort: it.antwort, kommentar: it.kommentar || "" }));
      await axios.post(`${API}/azav/antrag/${kursId}/self-check`, payload);
      toast.success("Self-Check gespeichert");
      load();
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-6 text-center"><Loader2 className="inline animate-spin" /></div>;

  const ampelColor = data.summary.ampel === "gruen" ? "bg-emerald-500"
    : data.summary.ampel === "gelb" ? "bg-yellow-400" : "bg-red-500";

  // Gruppieren nach Bereich
  const byArea = data.items.reduce((acc, it) => {
    (acc[it.bereich] ||= []).push(it); return acc;
  }, {});

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 flex items-center gap-6">
          <div className={`w-12 h-12 rounded-full ${ampelColor}`} />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Status</div>
            <div className="text-2xl font-semibold">
              {data.summary.ja} ja · {data.summary.nein} nein · {data.summary.na} n. a. · {data.summary.offen} offen
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="sc-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Antworten speichern
          </Button>
        </CardContent>
      </Card>

      {Object.entries(byArea).map(([bereich, items]) => (
        <Card key={bereich}>
          <CardHeader className="pb-3"><CardTitle className="text-base">{bereich}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Nr.</TableHead>
                  <TableHead>Prüfpunkt</TableHead>
                  <TableHead className="w-40">Antwort</TableHead>
                  <TableHead>Kommentar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.nr}>
                    <TableCell className="font-mono text-xs">{it.nr}</TableCell>
                    <TableCell className="text-sm">{it.frage}</TableCell>
                    <TableCell>
                      <Select value={it.antwort} onValueChange={v => setAnswer(it.nr, "antwort", v)}>
                        <SelectTrigger data-testid={`sc-ant-${it.nr}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="offen">offen</SelectItem>
                          <SelectItem value="ja">ja</SelectItem>
                          <SelectItem value="nein">nein</SelectItem>
                          <SelectItem value="na">nicht anwendbar</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        data-testid={`sc-kom-${it.nr}`}
                        placeholder="optional"
                        value={it.kommentar || ""}
                        onChange={e => setAnswer(it.nr, "kommentar", e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// -------- Helper: Maßnahmenliste-Export (Step 4) --------
const MassnahmenlisteStep = ({ kursId }) => {
  const downloadExcel = () => {
    oeffneMitLogin(`${API}/azav/antrag/${kursId}/massnahmenliste-xlsx`);
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Maßnahmenliste 09PAFB47</CardTitle>
        <CardDescription>
          Excel-Export mit 27 Spalten gemäß BA-Vorgabe. Übernimmt automatisch alle
          AZAV-Stammdaten (Schritt 1) und Kosten (Schritt 2).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="text-sm space-y-1 text-gray-700 list-disc list-inside">
          <li>Maßnahme-Titel & -Nummer</li>
          <li>KldB-2010-Code + Bezeichnung</li>
          <li>Durchführungsart, -form, Asynchron-Anteil</li>
          <li>UE-Aufteilung, Dauer, TN-Bereich</li>
          <li>Standort & Abschluss</li>
          <li>Kosten gesamt & Preis pro TN (aus Kalkulation)</li>
        </ul>
        <Button onClick={downloadExcel} data-testid="ml-download">
          <FileSpreadsheet className="w-4 h-4 mr-2" />09PAFB47 Excel herunterladen
        </Button>
      </CardContent>
    </Card>
  );
};

// -------- APV-Einreichungs-Center (Download-Sektor) --------
const STATUS_META = {
  verfuegbar: { label: "verfügbar", color: "bg-green-100 text-green-700 border-green-200", icon: "✓" },
  extern:     { label: "extern beizufügen", color: "bg-amber-100 text-amber-700 border-amber-200", icon: "" },
  entfaellt:  { label: "entfällt", color: "bg-gray-100 text-gray-600 border-gray-200", icon: "—" },
};

const APVEinreichungsCenter = ({ kursId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!kursId) return;
    setLoading(true);
    axios.get(`${API}/azav/antrag/${kursId}/einreichungs-uebersicht`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Einreichungs-Übersicht konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [kursId]);

  if (loading) {
    return (
      <Card className="border-emerald-300 bg-emerald-50/30">
        <CardContent className="py-8 flex items-center justify-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade APV-Einreichungs-Übersicht...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const items = data.items || [];
  const verfuegbar = items.filter(i => i.status === "verfuegbar").length;
  const extern = items.filter(i => i.status === "extern").length;

  return (
    <Card className="border-emerald-300 bg-emerald-50/30 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
          <Archive className="w-5 h-5" />
          APV-Einreichungs-Center — alle Unterlagen einzeln downloaden
        </CardTitle>
        <CardDescription className="text-emerald-800/80">
          Lade jedes Dokument einzeln herunter und lade es im APV-Portal hoch.<br/>
          <a href={data.upload_ziel_url} target="_blank" rel="noopener noreferrer"
             className="underline font-medium text-emerald-700 hover:text-emerald-900">
            ▸ Upload-Portal der APV öffnen
          </a>
          <span className="ml-3 text-xs">
            {verfuegbar} verfügbar · {extern} extern beizufügen
          </span>
        </CardDescription>
        {/* NEU: 1-Klick-Download aller Dokumente als ZIP */}
        <div className="mt-3">
          <Button
            variant="default"
            size="sm"
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="apv-zip-download-btn"
            onClick={async () => {
              try {
                toast.info("APV-ZIP-Paket wird gepackt…", { description: "Kann bis zu 30 Sek. dauern" });
                const res = await axios.get(
                  `${API}/azav/antrag/${kursId}/einreichungspaket-zip`,
                  { responseType: "blob", timeout: 120000 }
                );
                const blob = new Blob([res.data], { type: "application/zip" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `APV-Einreichung_${kursId}_${new Date().toISOString().split("T")[0]}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("ZIP-Paket heruntergeladen — enthält alle verfügbaren Dokumente + README");
              } catch (e) {
                toast.error("ZIP-Download fehlgeschlagen", { description: e?.message || String(e) });
              }
            }}
          >
            <Archive className="w-4 h-4 mr-2" />
            Alle Unterlagen als ZIP herunterladen
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map(item => {
          const meta = STATUS_META[item.status] || STATUS_META.verfuegbar;
          const canDownload = item.status === "verfuegbar" && item.endpoint;
          return (
            <div
              key={`${item.nr}-${item.titel}`}
              className="flex items-start gap-3 bg-white rounded border border-emerald-100 p-3"
              data-testid={`apv-einreichung-item-${item.nr}`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-900 font-semibold text-sm flex items-center justify-center">
                {item.nr}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900">{item.titel}</span>
                  <Badge variant="outline" className="text-[10px] uppercase font-mono">
                    {item.datei_typ}
                  </Badge>
                  <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
                    {meta.icon} {meta.label}
                  </Badge>
                </div>
                <div className="text-xs text-gray-600 mt-1">{item.beschreibung}</div>
                {item.hinweis && (
                  <div className="text-xs text-amber-700 mt-1 italic">{item.hinweis}</div>
                )}
              </div>
              <div className="flex-shrink-0">
                {canDownload ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => oeffneMitLogin(`${BACKEND_URL}${item.endpoint}`)}
                    data-testid={`apv-download-${item.nr}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled className="opacity-50">
                    <CircleSlash className="w-3.5 h-3.5 mr-1.5" />
                    {item.status === "extern" ? "manuell" : "n.z."}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};


// -------- Helper: Antragsmappe-ZIP (Step 5) --------
const MappeStep = ({ kursId }) => {
  const [status, setStatus] = useState(null);
  const [zipPreview, setZipPreview] = useState(null);
  const [uploading, setUploading] = useState(null); // code being uploaded
  const [bulkUploading, setBulkUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const loadStatus = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/azav/antrag/${kursId}/qm-bundle-status`),
        axios.get(`${API}/azav/antrag/${kursId}/zip-preview`),
      ]);
      setStatus(s.data);
      setZipPreview(p.data);
    } catch {
      toast.error("Status konnte nicht geladen werden");
    }
  };

  useEffect(() => { loadStatus(); }, [kursId]); // eslint-disable-line

  const uploadFile = async (code, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Nur PDF-Dateien erlaubt");
      return;
    }
    setUploading(code);
    try {
      const fd = new FormData();
      fd.append("code", code);
      fd.append("file", file);
      await axios.post(`${API}/azav/antrag/qm-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${code} hochgeladen`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(null);
    }
  };

  const bulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("files", f));
      const r = await axios.post(`${API}/azav/antrag/qm-upload-bulk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const skipped = r.data.results.filter(x => x.status !== "uploaded");
      toast.success(`${r.data.uploaded} / ${r.data.total} PDFs hochgeladen`
        + (skipped.length ? ` (${skipped.length} übersprungen)` : ""));
      if (skipped.length) {
        skipped.forEach(s => console.warn(`SKIP ${s.filename}: ${s.reason}`));
      }
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Upload fehlgeschlagen");
    } finally {
      setBulkUploading(false);
    }
  };

  const migrate = async () => {
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/azav/antrag/qm-migrate-to-db`);
      toast.success(`${r.data.migrated} Dateien in die DB migriert (${r.data.skipped} übersprungen)`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  const downloadZip = () => {
    oeffneMitLogin(`${API}/azav/antrag/${kursId}/zip`);
  };

  const allComplete = status && status.summary.fehlt === 0;

  // Liste der generierten PDF-Dokumente (kein Upload nötig — werden on-the-fly erzeugt)
  const generiertePDFs = [
    { code: "MK", titel: "Maßnahmekonzept", url: `${API}/kurse/${kursId}/massnahmekonzept-pdf`, beschreibung: "Komplettes AZAV-Maßnahmekonzept (Zielgruppe, Inhalte, Pädagogik, Zertifikat)" },
    { code: "CU", titel: "Curriculum", url: `${API}/kurse/${kursId}/curriculum-pdf`, beschreibung: "Modulübersicht mit UE-Aufteilung gemäß §16 BPflPersAPrV NRW" },
    { code: "SP", titel: "Stundenplan / Schulungsplan", url: `${API}/kurse/${kursId}/schulungsplan-pdf`, beschreibung: "Wochenraster der Maßnahme (FB_TN_15 für PFA-Extern)" },
    { code: "ML", titel: "Maßnahmenliste 09PAFB47", url: `${API}/azav/antrag/${kursId}/massnahmenliste-xlsx`, beschreibung: "Excel-Export mit allen Stammdaten für die FKS" },
  ];

  return (
    <div className="space-y-6">
      {/* APV-Einreichungs-Center — alle Unterlagen einzeln zum Download */}
      <APVEinreichungsCenter kursId={kursId} />

      {/* NEU: Generierte Dokumente — direkt einzeln anschauen/downloaden */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Generierte Dokumente — einzeln anschauen
          </CardTitle>
          <CardDescription>
            Diese PDFs werden automatisch aus den Stammdaten und der Kostenkalkulation erstellt
            und sind automatisch im finalen ZIP enthalten. Sie können sie aber auch jetzt einzeln öffnen.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {generiertePDFs.map(pdf => (
            <div key={pdf.code} className="rounded border bg-white p-3 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-[10px]">{pdf.code}</Badge>
                <div className="font-semibold text-sm">{pdf.titel}</div>
              </div>
              <div className="text-xs text-gray-500 flex-1">{pdf.beschreibung}</div>
              <div className="flex gap-2 mt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => oeffneMitLogin(pdf.url)}
                  data-testid={`gen-pdf-view-${pdf.code}`}
                  className="flex-1"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />Anschauen / Download
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Antragsmappe (ZIP) für die Fachkundige Stelle</CardTitle>
          <CardDescription>
            Bündelt alle Dokumente in einem ZIP — generierte PDFs (Maßnahmekonzept,
            Curriculum, Stundenplan, Kostenkalkulation, Maßnahmenliste) + Träger-QM-Dokumente.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-center">
          <Button onClick={downloadZip} size="lg" data-testid="zip-download" disabled={!allComplete}>
            <Archive className="w-5 h-5 mr-2" />Komplette Antragsmappe als ZIP
          </Button>
          {!allComplete && status && (
            <span className="text-sm text-amber-600 inline-flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {status.summary.fehlt} Dokument(e) fehlen — bitte unten hochladen
            </span>
          )}
          <Button variant="outline" onClick={migrate} disabled={migrating} data-testid="qm-migrate" className="ml-auto">
            {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Disk → DB migrieren
          </Button>
        </CardContent>
      </Card>

      {/* Bulk-Upload Card */}
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            Mehrere PDFs auf einmal hochladen (Bulk)
          </CardTitle>
          <CardDescription>
            Alle PDFs gleichzeitig auswählen — die Code-Erkennung erfolgt automatisch
            anhand des Dateinamens (z. B. <code className="text-xs bg-white px-1 rounded border">STD_01.pdf</code>,
            <code className="text-xs bg-white px-1 rounded border ml-1">FB_QM_03_Beschwerdeprotokoll.pdf</code>).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md cursor-pointer text-sm font-medium ${
            bulkUploading ? "bg-gray-300 text-gray-600 cursor-wait" : "bg-emerald-600 text-white hover:bg-emerald-700"
          }`} data-testid="qm-bulk-upload">
            {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            {bulkUploading ? "Lade hoch…" : "PDFs auswählen (Strg/⌘ + Klick für mehrere)"}
            <input
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={e => bulkUpload(e.target.files)}
              disabled={bulkUploading}
            />
          </label>
        </CardContent>
      </Card>

      {/* ZIP-Inhaltsvorschau */}
      {zipPreview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Archive className="w-5 h-5 text-emerald-600" />
              ZIP-Inhalt — diese {zipPreview.summary.total} Dateien werden gebündelt
            </CardTitle>
            <CardDescription>
              {zipPreview.summary.vorhanden} bereit · {zipPreview.summary.fehlt} fehlt noch
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Dateipfad im ZIP</TableHead>
                    <TableHead className="w-32">Quelle</TableHead>
                    <TableHead>Hinweis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {zipPreview.items.map(it => (
                    <TableRow key={it.pfad} className={!it.vorhanden ? "bg-red-50" : ""}>
                      <TableCell>
                        {it.vorhanden
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <CircleSlash className="w-4 h-4 text-red-600" />}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.pfad}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">{it.quelle}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{it.hinweis || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {status && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">QM-Dokumente im Bundle</CardTitle>
            <CardDescription>
              {status.summary.vorhanden} von {status.summary.total} vorhanden
              {status.summary.fehlt > 0 && (
                <span className="text-red-600"> · {status.summary.fehlt} fehlen — direkt hier hochladen</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {status.items.map(it => (
                <div
                  key={it.code}
                  className={`flex items-center gap-3 rounded border p-2.5 ${
                    it.vorhanden ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                  }`}
                >
                  {it.vorhanden ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <CircleSlash className="w-4 h-4 text-red-600 shrink-0" />
                  )}
                  <span className="font-mono text-sm font-semibold w-28 truncate">{it.code}</span>
                  {it.vorhanden && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] py-0 px-1">{it.quelle?.toUpperCase()}</Badge>
                      {it.groesse_kb} KB
                    </span>
                  )}
                  <div className="ml-auto">
                    <label
                      className={`inline-flex items-center gap-1.5 cursor-pointer text-xs px-2.5 py-1 rounded border ${
                        it.vorhanden ? "bg-white text-gray-600 border-gray-300 hover:bg-gray-50" : "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                      }`}
                      data-testid={`qm-upload-${it.code}`}
                    >
                      {uploading === it.code ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <FileText className="w-3 h-3" />
                      )}
                      {it.vorhanden ? "Ersetzen" : "Hochladen"}
                      <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={e => uploadFile(it.code, e.target.files?.[0])}
                        disabled={uploading === it.code}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =================== Main Wizard ===================
const AZAVAntragsWizard = () => {
  const [kurse, setKurse] = useState([]);
  const [kursId, setKursId] = useState("");
  const [step, setStep] = useState(1);
  const [stammdaten, setStammdaten] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kurse`)
      .then(r => {
        // Nur BG-fähige, AZAV-zugelassene Kurse zeigen
        const all = (r.data?.kurse || []).filter(k => k.bg_faehig && k.azav_zugelassen !== false);
        setKurse(all);
        setKursId((cur) => cur || all[0]?.id || "");
      })
      .catch(() => {});
  }, []);

  const kurs = useMemo(() => kurse.find(k => k.id === kursId), [kurse, kursId]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Wand2 className="w-7 h-7 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-semibold">AZAV-Antrags-Wizard</h1>
          <p className="text-sm text-gray-500">Schritt-für-Schritt-Assistent für die Beantragung neuer Maßnahmen bei der FKS</p>
        </div>
      </div>

      {/* Kurs-Auswahl */}
      <Card>
        <CardContent className="pt-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <Label className="md:w-32">Kurs / Maßnahme</Label>
          <Select value={kursId} onValueChange={setKursId}>
            <SelectTrigger data-testid="wiz-kurs-select" className="max-w-md"><SelectValue /></SelectTrigger>
            <SelectContent>
              {kurse.map(k => (
                <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {kurs?.bg_faehig && <Badge variant="default">BG-fähig</Badge>}
          {kurs && <Badge variant="outline">{kurs.ue} UE</Badge>}
        </CardContent>
      </Card>

      {/* Step-Navigator */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {STEPS.map(s => {
          const Icon = s.icon;
          const active = step === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setStep(s.id)}
              data-testid={`wiz-step-${s.id}`}
              className={`text-left p-3 rounded-lg border transition-all ${
                active ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200"
                       : "bg-white border-gray-200 hover:border-gray-400"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${active ? "text-emerald-600" : "text-gray-500"}`} />
                <span className="text-xs font-medium text-gray-400">Schritt {s.id}</span>
              </div>
              <div className={`font-semibold text-sm ${active ? "text-emerald-700" : "text-gray-700"}`}>{s.label}</div>
              <div className="text-xs text-gray-500 mt-1 line-clamp-2">{s.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {step === 1 && <StammdatenStep kursId={kursId} onChange={setStammdaten} />}
        {step === 2 && <KostenStep kursId={kursId} stammdaten={stammdaten} />}
        {step === 3 && <SelfCheckStep kursId={kursId} />}
        {step === 4 && <MassnahmenlisteStep kursId={kursId} />}
        {step === 5 && <MappeStep kursId={kursId} />}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          data-testid="wiz-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />Zurück
        </Button>
        <div className="text-sm text-gray-500 self-center">Schritt {step} von {STEPS.length}</div>
        <Button
          onClick={() => setStep(Math.min(STEPS.length, step + 1))}
          disabled={step === STEPS.length}
          data-testid="wiz-next"
        >
          Weiter<ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default AZAVAntragsWizard;
